// src/pages/SiteManagement.js - Updated with controlled inputs
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import api from "../../../services/apiClient";
import { storeUserData } from "../../../services/apiService";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import API_ENDPOINTS from "../../../config/apiEndpoints";

//for dual list box
import DualListBox, { normListId } from "../../common/dualListBox";
//for dropdown
import Dropdown from "../../common/dropDown";
//for datepicker
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
//for checkbox
import CheckBox from "../../common/checkBox";
//for gender
import GenderSelector from "../../common/GenderSelector";
//for image upload
import SimpleCircularUpload from "../../common/SimpleCircularUpload";

//utilities
import utils from "../../../utils/utils";
import FileUpload from "../../common/FileUpload";
import ConfirmModal from "../../common/ConfirmModal";
import AuthModal from "../../common/authmodal/authmodal";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import InternalUserAddEditModal from "./InternalUserAddEditModal";
import { mapStudyOptionsToAssignedRows } from "./StudyAssignmentPicker";
import {
  getActiveSiteLookup,
  siteToDropdownOptions,
} from "../../../services/lookupService";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  getEffectivePermissionKeys,
} from "../../../utils/permissionUtils";
import { sanitizeSelectedPermissionsForSave } from "../../../constants/qmsPermissions";
import { expandStoredQmsPermissionKeys } from "../qms/qmsPermissionUtils";
import { expandStoredIsfPermissionKeys } from "../../../constants/isfPermissionConsolidation";
import { filterAssignablePermissionKeys } from "../../../constants/permissions";
import {
  buildStudyColorLookup,
  pickStudyColorRaw,
} from "../../../utils/studyColorUtils";
import { fetchStudyMgmtList } from "../studymanagement/studyMgmtListService";
import "./userManagement.scss";
import { useExternalUserAccessScoping } from "./ExternalUserAccessScoping";
import { extractSuccessStory } from "../../../services/dmsMasterService";
import {
  formatUserTypeLabel,
  isSponsorCroUserRecord,
} from "../../../constants/userTypeLabels";
import {
  fetchUserList,
  mapUsersForTable,
  parseUserListResponse,
} from "../../../services/userManagementService";
import AdminChangeUserCredentialsModal from "../CredentialPolicy/AdminChangeUserCredentialsModal";
import UserStaffDocumentAccessModal from "./UserStaffDocumentAccessModal";
import {
  adminChangeUserAccessCode,
  adminChangeUserPassword,
  parseAdminCredentialChangeResponse,
} from "../../../services/credentialPolicyService";
import {
  validateNationalMobile,
  sanitizeNationalMobileInput,
  normalizeNationalMobileInput,
  orgMobileDialLabel,
} from "../../../utils/siteFieldValidationUtils";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import {
  getMfaSettings,
  isAuthenticatorEnrolled,
  resetAuthenticator,
} from "../../../services/mfaService";
import { organizationHasQmsModule, organizationHasQmsTrainingAdditionalRolesFeature } from "../../../utils/orgModuleAccess";
import {
  clearOrgPlatformFlagsHydrationAttempt,
  hydrateOrgPlatformFlagsIfNeeded,
} from "../../../services/orgPlatformFlagsHydrationService";
import { hasQmsActionPermission } from "../qms/qmsPermissionUtils";
import {
  fetchStaffDocumentLibraryDocuments,
  fetchStaffMyLearningDocuments,
} from "../qms/qmsService";
import {
  apiMessage,
  firstSuccessStoryRow,
  getApiTable,
  isApiSuccess,
} from "../qms/qmsApiUtils";

const padUniqueCodeNumber = (value, length = 5) => {
  if (value == null || value === "") return "";
  return String(value).padStart(length, "0");
};

const normalizeUniqueCodePurpose = (row = {}) =>
  String(row?.Perpose ?? row?.Purpose ?? row?.perpose ?? "")
    .trim()
    .toLowerCase();

const applyUniqueCodesFromEmpNum = (empNumList = []) => {
  const list = Array.isArray(empNumList) ? empNumList : [];
  const emp =
    list.find((item) => normalizeUniqueCodePurpose(item) === "employee") ||
    list.find((item) =>
      normalizeUniqueCodePurpose(item).includes("employee"),
    );
  const asc =
    list.find((item) => normalizeUniqueCodePurpose(item) === "ascension") ||
    list.find((item) =>
      normalizeUniqueCodePurpose(item).includes("ascension"),
    );
  const paddedEmp = padUniqueCodeNumber(emp?.NumberFormate, 5);
  return {
    prefix: emp?.Prefix || "EMP",
    employeeNumber:
      paddedEmp && paddedEmp !== "00000" ? paddedEmp : paddedEmp || "00001",
    employeeUniqueId: emp?.UniqueCodeId || "",
    ascNum: padUniqueCodeNumber(asc?.NumberFormate, 5),
    ascNumId: asc?.UniqueCodeId || "",
  };
};

const pickStudyIdFromRow = (s) => {
  if (!s || typeof s !== "object") return "";
  return (
    s.StudyId ??
    s.studyId ??
    s.StudyIdRef ??
    s.studyIdRef ??
    s.value ??
    ""
  );
};

const parseMappedSiteIds = (staff) => {
  const raw = staff?.MappedSiteIds ?? staff?.SiteIdRef ?? "";
  return String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

function normalizeRoleLabel(label) {
  return String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isSponsorOrCroRole(roleLabel) {
  const n = normalizeRoleLabel(roleLabel);
  return (
    n === "sponsor" ||
    n === "cro" ||
    n === "sponsor/cro" ||
    n === "sponsor cro"
  );
}

/** Super Admin: hidden for internal and external. System Administrator + PI: external (CRO) only. */
function isRoleOptionHiddenForUserType(roleLabel, isExternal) {
  const n = normalizeRoleLabel(roleLabel);
  if (n === "super admin") return true;

  const sponsorCroRole = isSponsorOrCroRole(roleLabel);

  if (isExternal) {
    if (n === "system administrator" || n === "principal investigator") {
      return true;
    }
    return !sponsorCroRole;
  }

  if (sponsorCroRole) return true;

  return false;
}

function isSystemAdministratorRole(role) {
  const label =
    typeof role === "object" && role !== null ? role.label : role ?? "";
  return normalizeRoleLabel(label) === "system administrator";
}

function getRoleIdValue(role) {
  if (typeof role === "object" && role !== null) return String(role.value ?? "").trim();
  return String(role ?? "").trim();
}

function mapAdditionalTrainingRolesFromApi(rows, roleOptions) {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((row) => {
      const value = String(row?.RoleId ?? row?.roleId ?? "").trim();
      if (!value) return null;
      const match = (roleOptions || []).find(
        (r) => String(r.value).toLowerCase() === value.toLowerCase(),
      );
      return match || { value, label: row?.Roles ?? row?.roles ?? value };
    })
    .filter(Boolean);
}

function buildAdditionalTrainingRoleOptions(roleOptions, primaryRole) {
  const primaryId = getRoleIdValue(primaryRole).toLowerCase();
  return (roleOptions || []).filter(
    (r) => String(r.value).toLowerCase() !== primaryId,
  );
}

function formatAdditionalTrainingRoleIdsForSave(selected) {
  return (selected || [])
    .map((r) => String(r?.value ?? "").trim())
    .filter(Boolean)
    .join(",");
}

const USER_MGMT_DEFAULT_AVATAR = "../images/avatar.png";
const USER_MGMT_CELL_MAX_LEN = 20;
const USER_MGMT_NAME_CELL_MAX_LEN = 30;

const isProfilePictureDataUrl = (value) =>
  String(value ?? "").trim().toLowerCase().startsWith("data:image");

const withProfilePictureCacheBust = (url) => {
  const raw = String(url ?? "").trim();
  if (!raw || isProfilePictureDataUrl(raw)) return raw;
  if (raw.toLowerCase().includes("avatar.png")) return raw;
  const sep = raw.includes("?") ? "&" : "?";
  return `${raw}${sep}_t=${Date.now()}`;
};

const truncateUserMgmtCell = (text, maxLen = USER_MGMT_CELL_MAX_LEN) => {
  const value = String(text ?? "").trim() || "—";
  if (value === "—" || value.length <= maxLen) {
    return { value, display: value };
  }
  return { value, display: `${value.slice(0, maxLen)}…` };
};

const formatTruncatedUserMgmtCell = (text, maxLen = USER_MGMT_CELL_MAX_LEN) => {
  const { value, display } = truncateUserMgmtCell(text, maxLen);
  return (
    <span className="user-mgmt-report__truncated" title={value}>
      {display}
    </span>
  );
};

const getUserEmployeeNum = (row) => String(row?.EmployeeNum ?? "").trim();

const formatUserMgmtName = (row) => {
  const name = String(row?.Name ?? "").trim();
  const emp = getUserEmployeeNum(row);
  if (emp && name) return `(${emp}) ${name}`;
  return emp || name || "—";
};

/** Normalize Active / Inactive (API may send Blocked, flags, etc.). */
const getUserMgmtStatusLabel = (row) => {
  const statusRaw = String(row?.Status ?? "").trim();
  if (statusRaw) {
    const n = statusRaw.toLowerCase();
    if (n === "active") return "Active";
    if (n === "inactive" || n === "blocked" || n === "archived") return "Inactive";
    return statusRaw;
  }
  const inactiveFlag = row?.Inactive ?? row?.InActive ?? row?.inactive;
  if (inactiveFlag === 1 || inactiveFlag === true || inactiveFlag === "1") {
    return "Inactive";
  }
  if (inactiveFlag === 0 || inactiveFlag === false || inactiveFlag === "0") {
    return "Active";
  }
  const account = String(row?.AccountStatus ?? "").trim().toLowerCase();
  if (account === "active" || account === "1") return "Active";
  if (account === "inactive" || account === "blocked" || account === "0") {
    return "Inactive";
  }
  return "—";
};

const USER_MGMT_FIELD_OPTIONS = [
  {
    key: "Name",
    label: "User",
    preferredWidth: 14,
    minWidth: 10,
    maxWidth: 22,
    grow: 2,
  },
  { key: "EmailId", label: "Email Id", grow: 2, maxWidth: 28 },
  { key: "DepartmentName", label: "Department", grow: 1, maxWidth: 22 },
  { key: "Roles", label: "Role", grow: 1, maxWidth: 22 },
  {
    key: "Status",
    label: "Status",
    preferredWidth: 7.5,
    minWidth: 6.5,
    maxWidth: 8,
    grow: 0,
    compact: true,
  },
  { key: "SystemDateTime", label: "Date Time", grow: 0, maxWidth: 14 },
];

const DEFAULT_USER_MGMT_COLUMNS = [
  "Name",
  "EmailId",
  "DepartmentName",
  "Roles",
  "Status",
  "SystemDateTime",
];

const USER_MGMT_COLUMN_CLASS = {
  Name: "user-mgmt-report__col-user",
  Status: "user-mgmt-report__col-status",
};

const USER_MGMT_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const USER_MGMT_COLUMN_DATE_PRESETS = DEFAULT_PRESETS;

const USER_MGMT_FILTERS_INITIAL = {
  roles: [],
  departments: [],
  status: "",
  dateRange: { ...USER_MGMT_EMPTY_DATE_RANGE },
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesUserMgmtDateRange = (value, range) => {
  if (
    !range ||
    range.preset === "All Time" ||
    (!range.startDate && !range.endDate)
  ) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw.toUpperCase() === "N/A") return false;

  const date = utils.parseDateInput(raw);
  if (!date) return false;

  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;

  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const hasUserProfilePicture = (row) => {
  const url = String(row?.ProfilePictureUrl ?? "").trim();
  if (!url) return false;
  const lower = url.toLowerCase();
  if (lower.includes("avatar.png") || lower.includes("/images/avatar")) return false;
  return true;
};

const sortFilterOptionValues = (values) =>
  [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

const buildUniqueFilterOptions = (rows, accessor) => {
  const set = new Set();
  for (const row of rows || []) {
    const value = String(row?.[accessor] ?? "").trim();
    if (value && value !== "—") set.add(value);
  }
  return sortFilterOptionValues([...set]).map((value) => ({ value, label: value }));
};

const buildUniqueStatusFilterOptions = (rows) => {
  // Always expose Active + Inactive so both can be filtered even if one set is empty.
  const set = new Set(["Active", "Inactive"]);
  for (const row of rows || []) {
    const value = getUserMgmtStatusLabel(row);
    if (value && value !== "—") set.add(value);
  }
  return sortFilterOptionValues([...set]).map((value) => ({ value, label: value }));
};

const applyUserMgmtFilters = (rows, filters) =>
  (rows || []).filter((row) => {
    const roleValues = filters.roles || [];
    if (roleValues.length) {
      const role = String(row?.Roles ?? "").trim();
      if (!roleValues.includes(role)) return false;
    }

    const deptValues = filters.departments || [];
    if (deptValues.length) {
      const dept = String(row?.DepartmentName ?? "").trim();
      if (!deptValues.includes(dept)) return false;
    }

    const statusFilter = filters.status || "";
    if (statusFilter && getUserMgmtStatusLabel(row) !== statusFilter) return false;

    if (!matchesUserMgmtDateRange(row?.SystemDateTime, filters.dateRange)) {
      return false;
    }

    return true;
  });

const USER_MGMT_CONTACT_CARD_WIDTH = 260;
const USER_MGMT_CONTACT_CARD_HEIGHT = 220;

const getContactCardPosition = (anchorRect) => {
  const margin = 8;
  const centerY = anchorRect.top + anchorRect.height / 2;
  let left = anchorRect.right + margin;
  if (left + USER_MGMT_CONTACT_CARD_WIDTH > window.innerWidth - margin) {
    left = anchorRect.left - USER_MGMT_CONTACT_CARD_WIDTH - margin;
  }
  left = Math.max(margin, Math.min(left, window.innerWidth - USER_MGMT_CONTACT_CARD_WIDTH - margin));

  let top = centerY;
  const halfHeight = USER_MGMT_CONTACT_CARD_HEIGHT / 2;
  top = Math.max(halfHeight + margin, Math.min(top, window.innerHeight - halfHeight - margin));

  return { top, left };
};

const UserMgmtAvatarImage = ({
  imgSrc,
  onImgError,
  className = "user-mgmt-report__avatar",
}) => (
  <img src={imgSrc} alt="" className={className} onError={onImgError} />
);

const UserMgmtIdentityCell = ({ row, onNameClick }) => {
  const anchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const retriedRef = useRef(false);
  const [cardPos, setCardPos] = useState(null);

  const remoteSrc = String(row?.ProfilePictureUrl ?? "").trim();
  const [imgSrc, setImgSrc] = useState(remoteSrc || USER_MGMT_DEFAULT_AVATAR);

  const name = String(row?.Name ?? "").trim() || "—";
  const emp = getUserEmployeeNum(row);
  const userName = formatUserMgmtName(row);
  const employeeNum = emp || "—";
  const email = String(row?.EmailId ?? "").trim() || "—";
  const role = String(row?.Roles ?? "").trim() || "—";
  const department = String(row?.DepartmentName ?? "").trim() || "—";
  const userType = String(row?.IsCro ?? "").trim() || "—";
  const status = getUserMgmtStatusLabel(row);
  const { display: nameDisplay } = truncateUserMgmtCell(
    name,
    USER_MGMT_NAME_CELL_MAX_LEN,
  );

  useEffect(() => {
    retriedRef.current = false;
    setImgSrc(remoteSrc || USER_MGMT_DEFAULT_AVATAR);
  }, [remoteSrc, row?.StaffId]);

  const handleImgError = (event) => {
    const failedSrc = String(event?.currentTarget?.src || "");
    if (
      !retriedRef.current &&
      remoteSrc &&
      !isProfilePictureDataUrl(remoteSrc) &&
      hasUserProfilePicture({ ProfilePictureUrl: remoteSrc }) &&
      !failedSrc.includes("_t=")
    ) {
      retriedRef.current = true;
      setImgSrc(withProfilePictureCacheBust(remoteSrc));
      return;
    }
    if (event?.currentTarget) {
      event.currentTarget.src = USER_MGMT_DEFAULT_AVATAR;
    }
    setImgSrc(USER_MGMT_DEFAULT_AVATAR);
  };

  const openCard = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCardPos(getContactCardPosition(rect));
  };

  const scheduleClose = () => {
    closeTimerRef.current = window.setTimeout(() => setCardPos(null), 160);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  return (
    <>
      <div
        ref={anchorRef}
        className="user-mgmt-report__user-cell has-contact-card"
        title={userName}
        onMouseEnter={openCard}
        onMouseLeave={scheduleClose}
        onFocus={openCard}
        onBlur={scheduleClose}
      >
        <span className="user-mgmt-report__avatar-wrap">
          <UserMgmtAvatarImage imgSrc={imgSrc} onImgError={handleImgError} />
        </span>
        <div className="user-mgmt-report__user-meta">
          <button
            type="button"
            className="report-table__cell-link user-mgmt-report__name-link"
            onClick={() => onNameClick?.(row)}
            title={`Open profile — ${name}`}
          >
            {nameDisplay}
          </button>
          {emp ? (
            <span className="user-mgmt-report__emp-badge" title={emp}>
              {emp}
            </span>
          ) : null}
        </div>
      </div>
      {cardPos
        ? createPortal(
            <div
              className="user-mgmt-report__contact-card"
              style={{
                top: `${cardPos.top}px`,
                left: `${cardPos.left}px`,
              }}
              role="tooltip"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <div className="user-mgmt-report__contact-card-head">
                <div className="user-mgmt-report__contact-card-photo">
                  <img
                    src={imgSrc}
                    alt={userName}
                    onError={handleImgError}
                  />
                </div>
                <div className="user-mgmt-report__contact-card-title">
                  <strong title={userName}>{userName}</strong>
                  <span>{userType}</span>
                </div>
              </div>
              <div className="user-mgmt-report__contact-card-body">
                <div>
                  <span>Employee #</span>
                  <strong>{employeeNum}</strong>
                </div>
                <div>
                  <span>Email</span>
                  <strong>{email}</strong>
                </div>
                <div>
                  <span>Role</span>
                  <strong>{role}</strong>
                </div>
                <div>
                  <span>Department</span>
                  <strong>{department}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{status}</strong>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

const UserManagement = () => {
  const { userData, refreshUserData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [tableFilters, setTableFilters] = useState(USER_MGMT_FILTERS_INITIAL);
  const [roles, setRoles] = useState([]);
  const [department, setDepartments] = useState([]);
  const [lineManager, setLineManagers] = useState([]);
  const [study, setStudyList] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  // Activate/Deactivate: confirm modal then password auth
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, row: null });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null); // { action, row } kept until auth API completes
  const [fieldErrors, setFieldErrors] = useState({});
  const [orgCountryIso2, setOrgCountryIso2] = useState("GB");
  const [formData, setFormData] = useState({
    StaffId: "",
    firstName: "",
    lastName: "",
    contactNumber: "",
    mobileCountryIso2: "GB",
    profilePicture: "",
    prefix: "EMP",
    employeeNumber: "00000",
    employeeUniqueId: "",
    ascNum: "",
    ascNumId: "",
    email: "",
    confirmEmail: "",
    dob: new Date(),
    startDate: new Date(),
    accessDate: new Date(),
    gender: "",
    role: "",
    department: "",
    lineManager: "",
    haveLineManagerRights: false,
    addressLine1: "",
    postalCode: "",
    city: "",
    county: "",
    country: "",
    studies: [],
    selectedSites: [],
    userPermissions: [],
    qmsTrainingAdditionalRoles: [],
    // user type for new-user creation
    // External == CRO user
    IsCRO: 0,
  });

  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Add-user type chooser removed — User Management always adds internal users.
  const [docAccessModal, setDocAccessModal] = useState({
    open: false,
    mode: "library",
    row: null,
  });

  const [adminCredForm, setAdminCredForm] = useState({
    open: false,
    row: null,
  });
  const [adminCredConfirm, setAdminCredConfirm] = useState({
    open: false,
    mode: null,
    row: null,
    value: "",
  });
  const [adminCredAuthOpen, setAdminCredAuthOpen] = useState(false);
  const [pendingAdminCred, setPendingAdminCred] = useState(null);
  const [mfaResetConfirm, setMfaResetConfirm] = useState({
    open: false,
    row: null,
  });
  const [mfaResetAuthOpen, setMfaResetAuthOpen] = useState(false);
  const [pendingMfaReset, setPendingMfaReset] = useState(null);
  /**
   * StaffId (upper) → authenticator enrolled.
   * undefined = not checked yet (action visible); false = hide; true = show.
   */
  const [mfaEnrolledByStaffId, setMfaEnrolledByStaffId] = useState({});
  const commonProfileCacheRef = useRef(null);
  const commonProfileLoadRef = useRef(null);
  /** Studies selected before switching to System Administrator (restored when leaving that role). */
  const studiesBeforeSysAdminRef = useRef(null);

  const externalScoping = useExternalUserAccessScoping({
    organizationId: userData?.OrganizationId,
    staffId: userData?.StaffId,
    studyOptions: study,
    showAlert,
    studies: formData.studies,
    onStudiesChange: (updater) =>
      setFormData((prev) => ({
        ...prev,
        studies:
          typeof updater === "function"
            ? updater(prev.studies || [])
            : updater,
      })),
  });

  const isExternalUser = isSponsorCroUserRecord(formData.IsCRO);

  const visibleRoles = useMemo(
    () =>
      (roles || []).filter(
        (r) => !isRoleOptionHiddenForUserType(r?.label, isExternalUser),
      ),
    [roles, isExternalUser],
  );

  const qmsTrainingAdditionalRoleOptions = useMemo(
    () => buildAdditionalTrainingRoleOptions(visibleRoles, formData.role),
    [visibleRoles, formData.role],
  );

  useEffect(() => {
    setFormData((prev) => {
      const r = prev.role;
      if (r == null || r === "") return prev;
      const roleValue =
        typeof r === "object" && r !== null ? r.value : r;
      if (roleValue == null || roleValue === "") return prev;
      const stillVisible = visibleRoles.some(
        (x) =>
          String(x.value).toLowerCase() === String(roleValue).toLowerCase(),
      );
      if (stillVisible) return prev;
      return { ...prev, role: "" };
    });
  }, [visibleRoles]);

  function mapAssignedStudyRow(studyRow, studyOptions = []) {
    const id = studyRow?.StudyId ?? studyRow?.StudyID ?? studyRow?.value;
    const matched = studyOptions.find(
      (item) =>
        String(item?.value ?? "").toLowerCase() ===
        String(id ?? "").toLowerCase(),
    );
    const num =
      studyRow?.StudyNumber ||
      matched?.studyNumber ||
      studyRow?.StudyCode ||
      "";
    const name = studyRow?.StudyName || matched?.studyName || "";
    const label =
      studyRow?.label ||
      matched?.label ||
      (num ? `${num} - ${name}` : name);
    const color =
      pickStudyColorRaw(studyRow) ||
      pickStudyColorRaw(matched) ||
      "";

    return {
      StudyId: id,
      StudyName: name,
      label,
      studyNumber: num,
      studyName: name,
      studyColor: color,
      StudySchColor:
        studyRow?.StudySchColor || matched?.StudySchColor || color,
      StudyColor: studyRow?.StudyColor || matched?.StudyColor || color,
    };
  }

  const getCommonUserProfileData = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return null;

    try {
      const [response, siteRows, rolePermsResponse, mgmtStudyRows, orgResponse] =
        await Promise.all([
        api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          PageName: "UserProfile",
        }),
        getActiveSiteLookup(userData),
        // UserProfile common-data SP does not return role Permission —
        // fetch it via the Role Management API and merge below.
        api
          .post(API_ENDPOINTS.ROLES.StaffRolesActivity, {
            OrganizationId: userData.OrganizationId,
            CreatedBy: userData.StaffId,
            ActionType: "GetStaffRoles",
            Roles: "",
            RoleId: "",
          })
          .catch(() => null),
        fetchStudyMgmtList({
          organizationId: userData.OrganizationId,
          staffIdRef: userData.StaffId,
          createdBy: userData.StaffId,
          action: "GetStudyMgmtLiveList",
        }).catch(() => []),
        fetchOrganizationSetup(userData.OrganizationId, userData.StaffId).catch(
          () => null,
        ),
      ]);

      if (orgResponse && Number(orgResponse?.success) === 1) {
        const { setupRow } = parseOrganizationSetupResponse(orgResponse);
        const iso = String(setupRow?.CountryIso2 || "GB")
          .trim()
          .toUpperCase();
        setOrgCountryIso2(iso || "GB");
      }

      const siteOptionsData = siteToDropdownOptions(siteRows).filter(
        (opt) => opt.value,
      );

      setSiteOptions(siteOptionsData);

      if (response.success === 1) {
        // GetStaffRoles returns StaffRoles.Id as RoleId while StaffRoleList
        // returns the RoleId GUID — merge by role name (unique per org).
        const rolePermissionByName = new Map(
          (rolePermsResponse?.data?.SuccessStory || []).map((item) => [
            String(item.Roles || "").trim().toLowerCase(),
            item.Permission || "",
          ]),
        );
        const rolesData = (response.data.StaffRoleList || []).map((item) => ({
          label: item.Roles,
          value: item.RoleId,
          permission:
            item.Permission ||
            rolePermissionByName.get(
              String(item.Roles || "").trim().toLowerCase(),
            ) ||
            "",
        }));
        const departmentsData = utils.mapToOptions(
          response.data.DepartmentList,
          "DepartmentName",
          "DepartmentId"
        );
        const lineManagersData = utils.mapToOptions(
          response.data.ApprovedLineManager,
          ["FirstName", "LastName"],
          "StaffId",
          " "
        );
        const studyColorLookup = buildStudyColorLookup(mgmtStudyRows);
        const studyData = (response.data.StudyList || []).map((s) => {
          const num = s?.StudyNumber || s?.StudyCode || s?.StudyNo || "";
          const name = s?.StudyName || "";
          const label = num ? `${num} - ${name}` : name;
          const color =
            pickStudyColorRaw(s) ||
            studyColorLookup.get(normListId(s?.StudyId)) ||
            "";
          return {
            label,
            value: s?.StudyId,
            studyNumber: num,
            studyName: name,
            studyColor: color,
            StudySchColor: color,
            StudyColor: s?.StudyColor || color,
          };
        });

        setRoles(rolesData);
        setDepartments(departmentsData);
        setLineManagers(lineManagersData);
        setStudyList(studyData);

        const uniqueCodes = applyUniqueCodesFromEmpNum(
          response.data.EmpNum || [],
        );
        setFormData((prev) => ({
          ...prev,
          ...uniqueCodes,
        }));

        const payload = {
          roles: rolesData,
          departments: departmentsData,
          lineManagers: lineManagersData,
          studies: studyData,
          siteOptions: siteOptionsData,
          uniqueCodes,
        };
        commonProfileCacheRef.current = payload;
        return payload;
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const ensureCommonUserProfileData = async ({ showLoading = false } = {}) => {
    if (commonProfileCacheRef.current) {
      return commonProfileCacheRef.current;
    }

    if (commonProfileLoadRef.current) {
      return commonProfileLoadRef.current;
    }

    const loadPromise = (async () => {
      try {
        if (showLoading) showLoader("Loading form options...");
        return await getCommonUserProfileData();
      } finally {
        if (showLoading) hideLoader();
        commonProfileLoadRef.current = null;
      }
    })();

    commonProfileLoadRef.current = loadPromise;
    return loadPromise;
  };

  // Load users and prefetch add/edit form lookups together on mount.
  useEffect(() => {
    if (userData) {
      getAllUsers();
      ensureCommonUserProfileData().catch(() => {});
    }
  }, [userData]);

  useEffect(() => {
    let cancelled = false;

    async function syncOrgPlatformFlags() {
      if (!userData?.StaffId || !userData?.OrganizationId) return;
      if (!organizationHasQmsModule(userData)) return;
      if (organizationHasQmsTrainingAdditionalRolesFeature(userData)) return;

      clearOrgPlatformFlagsHydrationAttempt();
      const updated = await hydrateOrgPlatformFlagsIfNeeded(userData);
      if (cancelled) return;

      if (
        updated?.QmsTrainingAdditionalRolesEnabled !==
        userData?.QmsTrainingAdditionalRolesEnabled
      ) {
        storeUserData(updated);
        refreshUserData();
      }
    }

    syncOrgPlatformFlags();

    return () => {
      cancelled = true;
    };
  }, [userData?.StaffId, userData?.OrganizationId, refreshUserData]);

  const getAllUsers = async (afterSave = null) => {
    if (!userData?.OrganizationId || !userData?.StaffId) {
      setUsersLoading(false);
      showAlert({
        type: "warning",
        title: "Session incomplete",
        message:
          "Your session is missing organisation or user details. Please sign out and sign in again.",
      });
      return;
    }

    try {
      setUsersLoading(true);
      const response = await fetchUserList({
        organizationId: userData.OrganizationId,
        staffId: userData.StaffId,
      });
      const parsed = parseUserListResponse(response);

      if (!parsed.ok) {
        setUsers([]);
        showAlert({
          type: "error",
          title: "Unable to load users",
          message: parsed.message,
        });
        return;
      }

      let nextUsers = mapUsersForTable(parsed.users)
        .filter(
          (user) =>
            !isSponsorCroUserRecord(user?.IsCRO ?? user?.IsCro),
        )
        .map((user) => ({
          ...user,
          IsCro: formatUserTypeLabel(user?.IsCRO ?? user?.IsCro),
        }));

      // After save: show uploaded photo immediately (API URL can lag / cache).
      if (afterSave?.staffId || afterSave?.email || afterSave?.previewDataUrl) {
        const staffId = String(afterSave.staffId || "").toUpperCase();
        const email = String(afterSave.email || "").trim().toLowerCase();
        const preview = afterSave.previewDataUrl || "";
        nextUsers = nextUsers.map((user) => {
          const matchStaff =
            staffId &&
            String(user?.StaffId || "").toUpperCase() === staffId;
          const matchEmail =
            email &&
            String(user?.EmailId || "").trim().toLowerCase() === email;
          if (!matchStaff && !matchEmail) return user;

          const remoteUrl = String(user?.ProfilePictureUrl ?? "").trim();
          if (hasUserProfilePicture(user)) {
            return {
              ...user,
              ProfilePictureUrl: withProfilePictureCacheBust(remoteUrl),
            };
          }
          if (preview) {
            return { ...user, ProfilePictureUrl: preview };
          }
          return user;
        });
      }

      setUsers(nextUsers);
      setMfaEnrolledByStaffId({});
    } catch (error) {
      setUsers([]);
      setMfaEnrolledByStaffId({});
      showAlert({
        type: "error",
        title: "Unable to load users",
        message:
          error?.message ||
          "We could not load the user list. Check that the API is running and try again.",
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRefresh = () => {
    getAllUsers();
  };

  const handleEdit = async (row) => {
    try {
      if (!row || row === "") {
        // User Management adds internal staff only; Sponsor/CRO users are added
        // from Sponsor/CRO management.
        await openUserPopupAfterType(false);
        return;
      }

      const commonData = await ensureCommonUserProfileData({ showLoading: true });
      if (!commonData) return;

      setFormData((prev) => ({
        ...prev,
        StaffId: row.StaffId,
      }));

      await getUserByStaffId(row.StaffId, commonData);
      setIsUserPopupOpen(true);
    } catch (error) {
      console.error("Error in handleEdit:", error);
    }
  };

  const openUserPopupAfterType = async (isExternal) => {
    try {
      // Always refresh EmpNum for Add — cache can leave stale / blank employee numbers.
      commonProfileCacheRef.current = null;
      studiesBeforeSysAdminRef.current = null;
      const commonData = await ensureCommonUserProfileData({
        showLoading: true,
      });
      if (!commonData) {
        showAlert({
          title: "Unable to open form",
          message: "Form options could not be loaded. Please try again.",
          type: "danger",
        });
        return;
      }

      const uniqueCodes =
        commonData.uniqueCodes ||
        applyUniqueCodesFromEmpNum([]);

      setFormData((prev) => ({
        ...prev,
        StaffId: "",
        firstName: "",
        lastName: "",
        contactNumber: "",
        mobileCountryIso2: orgCountryIso2 || "GB",
        profilePicture: "",
        email: "",
        confirmEmail: "",
        dob: new Date(),
        startDate: new Date(),
        accessDate: new Date(),
        gender: "",
        role: "",
        department: "",
        lineManager: "",
        haveLineManagerRights: false,
        addressLine1: "",
        postalCode: "",
        city: "",
        county: "",
        country: "",
        studies: [],
        selectedSites: [],
        userPermissions: [],
        qmsTrainingAdditionalRoles: [],
        ...uniqueCodes,
        IsCRO: isExternal ? 1 : 0,
      }));
      setFieldErrors({});
      setCurrentStep(1);
      setIsUserPopupOpen(true);
    } catch (error) {
      console.error("Error opening add-user form:", error);
    }
  };

  const getUserByStaffId = async (StaffId, commonData) => {
    if (!userData?.OrganizationId || !userData?.StaffId || !commonData) return;
    showLoader("Fetching user profile..");
    try {
      const response = await api.post(API_ENDPOINTS.USER.GET_INDIVIDUAL_USER, {
        OrganizationId: userData.OrganizationId,
        StaffIDRef: userData.StaffId,
        CreatedBy: userData.StaffId,
        StaffId: StaffId,
        Action: "GetProfileDetails",
      });

      if (response.success === 1) {
        const staff = response.data.StaffRecord[0];
        const mappedSiteIds = parseMappedSiteIds(staff);
        const availableSites = commonData.siteOptions || siteOptions;
        const selectedSites = availableSites.filter((opt) =>
          mappedSiteIds.some(
            (id) =>
              String(id).toLowerCase() === String(opt.value).toLowerCase(),
          ),
        );
        const matchedRole =
          commonData.roles.find(
            (r) => r.value.toLowerCase() === staff["RoleIdRef"]?.toLowerCase(),
          ) || null;
        const storedUserPerms = staff?.UserPermission
          ? parsePermissions(staff.UserPermission)
          : getEffectivePermissionKeys(
              matchedRole?.label,
              matchedRole?.permission,
            );
        setFormData((prev) => ({
          ...prev,
          firstName: staff.Forename?.trim() || "",
          lastName: staff.Surname?.trim() || "",
          mobileCountryIso2: String(
            staff.MobileCountryIso2 || orgCountryIso2 || "GB",
          )
            .trim()
            .toUpperCase(),
          contactNumber: sanitizeNationalMobileInput(
            staff["Contact Number"] || "",
            staff.MobileCountryIso2 || orgCountryIso2 || "GB",
          ),
          profilePicture: staff.ProfilePictureUrl || "",
          prefix: staff["Employee Number"].split(" ")[0],
          employeeNumber: staff["Employee Number"].split(" ")[1],
          email: staff["Email Id"] || "",
          confirmEmail: staff["Email Id"] || "",
          dob: staff["Date of birth"]
            ? new Date(utils.parseDDMMYYYY(staff["Date of birth"]))
            : null,
          startDate: staff["Start Date"] ? new Date(staff["Start Date"]) : null,
          accessDate: staff["Access Date"]
            ? new Date(staff["Access Date"])
            : null,
          gender: staff.Gender || null,
          role: matchedRole,
          department:
            commonData.departments.find(
              (r) =>
                r.value.toLowerCase() === staff["DepartmentId"]?.toLowerCase()
            ) || null,
          lineManager:
            commonData.lineManagers.find(
              (r) =>
                r.value.toLowerCase() === staff["LineManagerId"]?.toLowerCase()
            ) || null,
          haveLineManagerRights:
            staff["Line Manager Rights"]?.toLowerCase() === "yes",
          // External user == CRO user
          IsCRO: staff?.IsCRO ?? staff?.IsCro ?? 0,
          addressLine1: staff["H No"] || "",
          postalCode: staff["Post Code"] || "",
          city: staff.City || "",
          county: staff.County || "",
          country: staff.Country || "",
          studies: (response.data.StudyList || []).map((s) =>
            mapAssignedStudyRow(s, commonData.studies),
          ),
          selectedSites,
          userPermissions: expandStoredIsfPermissionKeys(
            expandStoredQmsPermissionKeys(
              filterAssignablePermissionKeys(storedUserPerms),
            ),
          ),
          qmsTrainingAdditionalRoles: mapAdditionalTrainingRolesFromApi(
            response.data?.AdditionalTrainingRoles,
            commonData.roles,
          ),
        }));
      }
    } catch (error) {
    } finally {
      hideLoader();
    }
  };

  const handleClose = () => {
    setIsUserPopupOpen(false);
    setFieldErrors({});
    resetForm();
  };

  const resetForm = () => {
    setCurrentStep(1);
    studiesBeforeSysAdminRef.current = null;
    setFormData({
      StaffId: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      mobileCountryIso2: orgCountryIso2 || "GB",
      profilePicture: "",
      prefix: "EMP",
      employeeNumber: "00000",
      employeeUniqueId: "",
      ascNum: "",
      ascNumId: "",
      email: "",
      confirmEmail: "",
      dob: new Date(),
      startDate: new Date(),
      accessDate: new Date(),
      gender: "",
      role: "",
      department: "",
      lineManager: "",
      haveLineManagerRights: false,
      addressLine1: "",
      postalCode: "",
      city: "",
      county: "",
      country: "",
      studies: [],
      selectedSites: [],
      userPermissions: [],
      qmsTrainingAdditionalRoles: [],
      IsCRO: 0,
    });
    externalScoping.reset();
  };

  const [originalStudies, setOriginalStudies] = useState([]);

  // Extract file extension from base64 image
  const getFileExtensionFromBase64 = (base64String) => {
    if (!base64String) return "";
    const match = base64String.match(/data:image\/(\w+);base64,/);
    return match ? match[1] : "";
  };

  // Calculate removed studies
  const getRemovedStudies = () => {
    const currentStudyIds = formData.studies?.map((s) => s.StudyId) || [];
    const originalStudyIds = originalStudies?.map((s) => s.StudyId) || [];

    return originalStudies
      .filter((study) => !currentStudyIds.includes(study.StudyId))
      .map((study) => ({
        StudyId: study.StudyId,
        StudyName: study.StudyName || "",
      }));
  };

  /** External (CRO) user → 1, internal → 0 for SAVE_UPDATE_USER */
  const getIsCroForApi = () => {
    const v = formData?.IsCRO;
    if (v === 1 || v === 2 || v === true) return 1;
    if (v === 0 || v === false) return 0;
    const s = String(v ?? "").trim().toLowerCase();
    if (s === "1" || s === "2" || s === "true") return 1;
    return 0;
  };

  const createStaffRegistrationPayload = () => {
    const payload = {
      StaffId: formData.StaffId || "",
      FirstName: formData.firstName || "",
      LastName: formData.lastName || "",
      EmployeeNum: formData.employeeNumber,
      RoleIdRef: formData.role?.value || "",
      Gender: formData.gender || "",
      StartDate: formData.startDate
        ? formData.startDate.toLocaleDateString()
        : "",
      AccessDate: formData.accessDate
        ? formData.accessDate.toLocaleDateString()
        : "",
      DOB: formData.dob ? formData.dob.toLocaleDateString() : "",
      EmailId: (formData.email || "").trim(),
      ContactNumber: formData.contactNumber || "",
      MobileCountryIso2: String(
        formData.mobileCountryIso2 || orgCountryIso2 || "GB",
      )
        .trim()
        .toUpperCase(),
      AddressLine1: formData.addressLine1 || "",
      City: formData.city || "",
      PostCode: formData.postalCode || "",
      County: formData.county || "",
      Country: formData.country || "",
      HaveLineManagerRights: formData.haveLineManagerRights ? "Yes" : "No",
      Action: formData.StaffId === "" ? "Add" : "Update",
      LineManagerId: formData.lineManager?.value || "",
      DepartmentId: formData.department?.value || "",
      // System Administrator: full study access — do not persist StudyList.
      ...(isSystemAdministratorRole(formData.role)
        ? {}
        : {
            StudyList:
              formData.studies?.map((studyItem) => ({
                StudyId: studyItem.StudyId,
                StudyName: studyItem.StudyName || studyItem.label || "",
              })) || [],
          }),
      //RemovedStudyList: getRemovedStudies(),
      OrganizationId: userData?.OrganizationId || "",
      CreatedById: userData?.StaffId || "",
      LoggedInUserName:
        userData?.FirstName + " " + userData?.LastName || userData?.Name || "",
      OrganizationName: userData?.OrganizationName || "",
      DocumentList: "",
      Base64Image: formData.profilePicture || "",
      Extension: getFileExtensionFromBase64(formData.profilePicture), // extract from base64
      IsCRO: getIsCroForApi(),
    };

    return payload;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  /** Role may be stored as dropdown object or string (handleRoles). */
  const getRoleIdForValidation = () => {
    const r = formData.role;
    if (r == null || r === "") return "";
    if (typeof r === "string") return r.trim();
    return String(r?.value ?? "").trim();
  };

  /** Fields marked with * — returns { fieldKey: message } for inline errors */
  const getBasicInfoValidationErrorMap = () => {
    const errors = {};
    const trim = (v) => String(v ?? "").trim();

    if (!trim(formData.firstName)) errors.firstName = "First name is required.";
    if (!trim(formData.lastName)) errors.lastName = "Last name is required.";

    const mobileCountryIso2 = String(
      formData.mobileCountryIso2 || orgCountryIso2 || "GB",
    )
      .trim()
      .toUpperCase();
    const contactResult = validateNationalMobile(formData.contactNumber, {
      countryIso2: mobileCountryIso2,
      required: false,
    });
    if (!contactResult.valid) {
      errors.contactNumber = contactResult.message;
    }

    const empNo = trim(formData.employeeNumber);
    if (!empNo || empNo === "00000") {
      errors.employeeNumber = "Employee number is required.";
    }

    const email = trim(formData.email);
    if (!email) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    const confirm = trim(formData.confirmEmail);
    if (email && !confirm) {
      errors.confirmEmail = "Confirm email is required.";
    } else if (
      email &&
      confirm &&
      email.toLowerCase() !== confirm.toLowerCase()
    ) {
      errors.confirmEmail = "Email and confirm email must match.";
    }

    if (!getRoleIdForValidation()) errors.role = "Role is required.";

    if (!isExternalUser && !isSystemAdministratorRole(formData.role)) {
      const selected = formData.selectedSites || [];
      if (!Array.isArray(selected) || selected.length === 0) {
        errors.selectedSites = "At least one site is required.";
      }
    }

    if (getIsCroForApi() === 1) {
      const shareError = externalScoping.getStudyShareValidationError();
      if (shareError) errors.studies = shareError;
    }

    return errors;
  };

  const getBasicInfoValidationErrors = () =>
    Object.values(getBasicInfoValidationErrorMap());

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const saveUserPermissionsForStaff = async (targetStaffId) => {
    if (!userData?.OrganizationId || !userData?.StaffId || !targetStaffId) {
      return { ok: true };
    }

    // System Administrator also saves selections — admins may limit access.
    const permissionPayload = sanitizeSelectedPermissionsForSave(
      filterAssignablePermissionKeys(formData.userPermissions || []),
    ).join(",");

    const response = await api.post(API_ENDPOINTS.USER.GET_INDIVIDUAL_USER, {
      OrganizationId: userData.OrganizationId,
      StaffIDRef: userData.StaffId,
      CreatedBy: userData.StaffId,
      StaffId: targetStaffId,
      Action: "UpdateuserPermission",
      Permission: permissionPayload,
    });

    if (Number(response?.success) === 1) {
      return { ok: true };
    }

    const message =
      response?.data?.[0]?.MSG ||
      response?.message ||
      "User was saved but permissions could not be updated.";
    return { ok: false, message };
  };

  const handleUserSaveUpdate = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;

    const errorMap = getBasicInfoValidationErrorMap();
    if (Object.keys(errorMap).length > 0) {
      setFieldErrors(errorMap);
      setCurrentStep(1);
      return;
    }
    setFieldErrors({});

    const isCroPayload = getIsCroForApi();
    if (isCroPayload === 1) {
      const shareError = externalScoping.getStudyShareValidationError();
      if (shareError) {
        setFieldErrors({ studies: shareError });
        return;
      }
    }
    try {
      showLoader(formData.StaffId === "" ? "Saving user..." : "Updating user...");
      let documentListPayload = "";
      let externalStaffIsfDocumentsPayload = [];
      if (isCroPayload === 1) {
        [documentListPayload, externalStaffIsfDocumentsPayload] =
          await Promise.all([
            externalScoping.buildCroDocumentListString(),
            externalScoping.buildExternalStaffIsfDocumentsPayload(),
          ]);
      }
      const response = await api.post(API_ENDPOINTS.USER.SAVE_UPDATE_USER, {
        StaffId: formData.StaffId.toUpperCase() || "",
        FirstName: formData.firstName || "",
        LastName: formData.lastName || "",
        EmployeeNum: formData.employeeNumber,
        // Empty string (not null) — API SqlParameter binding rejects null for these fields.
        EmployeeUniqueId: formData.employeeUniqueId || "",
        AscNum: formData.ascNum || "",
        AscNumId: formData.ascNumId || "",
        RoleIdRef: getRoleIdForValidation().toUpperCase() || "",
        Gender: formData.gender || "",
        StartDate: formData.startDate ? formatDate(formData.startDate) : "",
        AccessDate: formData.accessDate ? formatDate(formData.accessDate) : "",
        DOB: formData.dob ? formatDate(formData.dob) : "",
        EmailId: (formData.email || "").trim(),
        ContactNumber: (() => {
          const mobileCountryIso2 = String(
            formData.mobileCountryIso2 || orgCountryIso2 || "GB",
          )
            .trim()
            .toUpperCase();
          const result = validateNationalMobile(formData.contactNumber, {
            countryIso2: mobileCountryIso2,
            required: false,
          });
          return result.national || formData.contactNumber || "";
        })(),
        MobileCountryIso2: String(
          formData.mobileCountryIso2 || orgCountryIso2 || "GB",
        )
          .trim()
          .toUpperCase(),
        AddressLine1: formData.addressLine1 || "",
        City: formData.city || "",
        PostCode: formData.postalCode || "",
        County: formData.county || "",
        Country: formData.country || "",
        HaveLineManagerRights: formData.haveLineManagerRights ? 1 : 0,
        Action: formData.StaffId === "" ? "Insert" : "Update",
        LineManagerId: formData.lineManager?.value || "",
        DepartmentId: formData.department?.value || "",
        // System Administrator: full study access — do not persist StudyList.
        ...(isSystemAdministratorRole(formData.role)
          ? {}
          : {
              StudyList:
                formData.studies?.map((studyItem) => ({
                  StudyId: studyItem.StudyId,
                  StudyName: studyItem.label || "",
                })) || [],
            }),
        ...(isCroPayload === 0 && !isSystemAdministratorRole(formData.role)
          ? {
              SiteList: (formData.selectedSites || []).map((site) => ({
                SiteId: site?.value || site?.SiteId || "",
                SiteName: site?.label || site?.SiteName || "",
              })),
            }
          : {}),
        //RemovedStudyList: getRemovedStudies(),
        OrganizationId: userData?.OrganizationId.toUpperCase() || "",
        CreatedById: userData?.StaffId.toUpperCase() || "",
        LoggedInUserName: userData.UserName || "",
        OrganizationName: userData?.OrganizationName || "",
        // DocumentList: documentListPayload,
        DocumentsXML: documentListPayload,
        ...(isCroPayload === 1
          ? { ExternalStaffISFDocuments: externalStaffIsfDocumentsPayload }
          : {}),
        Base64Image: formData.profilePicture || "",
        Extension: getFileExtensionFromBase64(formData.profilePicture), // extract from base64
        IsCRO: isCroPayload,
        ...(orgHasQmsTrainingAdditionalRoles && isCroPayload === 0
          ? {
              AdditionalTrainingRoleIds: formatAdditionalTrainingRoleIdsForSave(
                formData.qmsTrainingAdditionalRoles,
              ),
            }
          : {}),
      });

      const procRows = extractSuccessStory(response);
      const procRow = procRows[0] ?? response?.data?.SuccessStory?.[0];
      const procOk =
        Number(response?.success) === 1 ||
        (procRow != null &&
          (Number(procRow.Success) === 1 || procRow.Success === true));
      const procFail =
        Number(response?.success) === 0 ||
        (procRow != null &&
          (Number(procRow.Success) === 0 || procRow.Success === false));

      if (procFail) {
        const failMessage = procRow?.MSG || response?.message || "Failed to save user.";
        if (
          formData.StaffId === "" &&
          /employee number already exists/i.test(failMessage)
        ) {
          await getCommonUserProfileData();
        }
        showAlert({
          title: "Error",
          message: failMessage,
          type: "danger",
        });
        return;
      }

      if (procOk || response?.success === 1) {
        let successMessage =
          procRow?.MSG ||
          response?.message ||
          (formData.StaffId === ""
            ? "User has been saved successfully."
            : "User has been updated successfully.");

        if (isCroPayload === 0 && !isExternalUser) {
          const targetStaffId = String(
            formData.StaffId || procRow?.StaffId || "",
          ).toUpperCase();

          if (targetStaffId) {
            try {
              const permResult = await saveUserPermissionsForStaff(targetStaffId);
              if (!permResult.ok) {
                successMessage = `${successMessage}\n\n${permResult.message}`;
              }
            } catch (permError) {
              console.error("Save user permissions error:", permError);
              successMessage = `${successMessage}\n\nUser was saved but permissions could not be updated.`;
            }
          }
        }

        showAlert({
          title: "Success",
          message: successMessage,
          type: "success",
        });
        const previewDataUrl = isProfilePictureDataUrl(formData.profilePicture)
          ? formData.profilePicture
          : "";
        const savedStaffId = String(
          formData.StaffId || procRow?.StaffId || "",
        ).toUpperCase();
        const savedEmail = String(formData.email || "").trim();
        commonProfileCacheRef.current = null;
        handleClose();
        await getAllUsers({
          staffId: savedStaffId,
          email: savedEmail,
          previewDataUrl,
        });
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Failed to save user.",
          type: "danger",
        });
      }
    } catch (err) {
      console.error("Save/update user error:", err);
      showAlert({
        title: "Error",
        message: err?.response?.data?.message || err?.message || "Failed to save user.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 5);

  const handleRoles = (role) => {
    const roleId =
      typeof role === "object" && role !== null ? role.value : role;
    const roleRecord = (roles || []).find(
      (item) =>
        String(item.value).toLowerCase() === String(roleId || "").toLowerCase(),
    );
    const roleLabel =
      (typeof role === "object" && role?.label) || roleRecord?.label || "";
    const rolePerms = isSystemAdministratorRole(roleLabel)
      ? filterAssignablePermissionKeys(getAllPermissionKeys())
      : expandStoredIsfPermissionKeys(
          expandStoredQmsPermissionKeys(
            filterAssignablePermissionKeys(
              getEffectivePermissionKeys(roleLabel, roleRecord?.permission),
            ),
          ),
        );

    const isSysAdminRole = isSystemAdministratorRole(roleLabel);
    clearFieldError("role");
    setFormData((prev) => {
      const wasSysAdmin = isSystemAdministratorRole(prev.role);
      let nextStudies = prev.studies || [];
      if (isSysAdminRole && !wasSysAdmin) {
        studiesBeforeSysAdminRef.current = Array.isArray(prev.studies)
          ? [...prev.studies]
          : [];
        nextStudies = mapStudyOptionsToAssignedRows(study);
      } else if (!isSysAdminRole && wasSysAdmin) {
        nextStudies =
          studiesBeforeSysAdminRef.current != null
            ? studiesBeforeSysAdminRef.current
            : prev.studies || [];
        studiesBeforeSysAdminRef.current = null;
      }
      return {
        ...prev,
        role: role || "",
        userPermissions: rolePerms,
        studies: nextStudies,
        qmsTrainingAdditionalRoles: (prev.qmsTrainingAdditionalRoles || []).filter(
          (item) =>
            String(item?.value ?? "").toLowerCase() !==
            String(roleId || "").toLowerCase(),
        ),
      };
    });
  };

  const handleQmsTrainingAdditionalRoles = (selected) => {
    const primaryId = getRoleIdValue(formData.role).toLowerCase();
    const next = (selected || []).filter(
      (item) => String(item?.value ?? "").toLowerCase() !== primaryId,
    );
    setFormData((prev) => ({
      ...prev,
      qmsTrainingAdditionalRoles: next,
    }));
  };

  const handleUserPermissionsChange = (permissions) => {
    setFormData((prev) => ({
      ...prev,
      userPermissions: filterAssignablePermissionKeys(permissions),
    }));
  };

  const handlStartDate = (date) => {
    setFormData((prev) => ({
      ...prev,
      startDate: date,
    }));
  };

  const handlAccessDate = (date) => {
    setFormData((prev) => ({
      ...prev,
      accessDate: date,
    }));
  };

  const handlDOB = (date) => {
    setFormData((prev) => ({
      ...prev,
      dob: date,
    }));
  };

  const handleDepartment = (department) => {
    setFormData((prev) => ({
      ...prev,
      department: department || "",
    }));
  };

  const handleLineManager = (lineManager) => {
    setFormData((prev) => ({
      ...prev,
      lineManager: lineManager || "",
    }));
  };

  const handleSites = (selected) => {
    clearFieldError("selectedSites");
    setFormData((prev) => ({
      ...prev,
      selectedSites: Array.isArray(selected) ? selected : [],
    }));
  };

  const handleFormInput = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name) clearFieldError(name);
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    });
  };

  const selectedStudyIdsForDualList = useMemo(
    () =>
      formData.studies
        ?.map((s) => normListId(pickStudyIdFromRow(s)))
        .filter(Boolean) || [],
    [formData.studies],
  );

  const handleGenderChange = (gender) => {
    setFormData((prev) => ({
      ...prev,
      gender: gender || "",
    }));
  };

  const handleView = (row) => {
    navigate(`/userprofile/${row.StaffId}`);
  };

  const isActive = (row) => {
    const s = (row?.Status || row?.AccountStatus || "").toString().trim().toLowerCase();
    return s === "active";
  };

  const handleToggleStatusClick = (row) => {
    const action = isActive(row) ? "deactivate" : "activate";
    setConfirmModal({ open: true, action, row });
  };

  const handleConfirmClose = () => {
    setConfirmModal({ open: false, action: null, row: null });
  };

  const handleConfirmYes = () => {
    const { action, row } = confirmModal;
    setPendingToggle({ action, row });
    setAuthModalOpen(true);
    handleConfirmClose();
  };

  const handleAuthClose = () => {
    setAuthModalOpen(false);
    setPendingToggle(null);
  };

  const handleAuthSubmit = async (authPayload) => {
    if (!pendingToggle?.row || !pendingToggle?.action) return;
    const { row, action } = pendingToggle;

    try {
      showLoader("Updating user status...");
      const response = await api.post(API_ENDPOINTS.USER.ACTIVATE_DEACTIVATE_USER, {
        OrganizationId: userData.OrganizationId,
        StaffId: row.StaffId,
        Action: action === "activate" ? "ActiveUser" : "BlockedUser",
        CreatedBy: userData.StaffId,
      });

      setPendingToggle(null);
      setAuthModalOpen(false);
      getAllUsers();

      const tableMessage = response?.data?.Table?.[0]?.Message;
      const isSuccess = response?.success === 1;

      if (isSuccess) {
        showAlert({
          title: "Success",
          message: tableMessage || response?.message || "User status updated successfully.",
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message: tableMessage || response?.message || "Failed to update user status.",
          type: "danger",
        });
      }
    } catch (err) {
      console.error("Activate/Deactivate user error:", err);
      showAlert({
        title: "Error",
        message: err?.response?.data?.message || err?.message || "Failed to update user status.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const isSystemAdmin = userData?.Role === "System Administrator";
  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");
  const canChangeCredentials =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "CHANGE_CREDENTIALS"));
  const canResetMfa =
    !!userData &&
    (isSystemAdmin ||
      hasPermission(userPermissions, "CHANGE_CREDENTIALS") ||
      hasPermission(userPermissions, "USER_MANAGEMENT") ||
      hasPermission(userPermissions, "UPDATE_USER"));

  const staffMfaKey = (staffId) => String(staffId || "").toUpperCase();

  // Load which users have authenticator enrolled so Reset only appears for them.
  useEffect(() => {
    if (!canResetMfa || !users.length) return undefined;

    let cancelled = false;
    const staffIds = [
      ...new Set(
        users
          .map((u) => String(u?.StaffId || "").trim())
          .filter(Boolean),
      ),
    ];

    const hydrate = async () => {
      const CHUNK = 8;
      for (let i = 0; i < staffIds.length; i += CHUNK) {
        if (cancelled) return;
        const chunk = staffIds.slice(i, i + CHUNK);
        const entries = await Promise.all(
          chunk.map(async (id) => {
            try {
              const response = await getMfaSettings(id);
              return [staffMfaKey(id), isAuthenticatorEnrolled(response)];
            } catch {
              return [staffMfaKey(id), false];
            }
          }),
        );
        if (cancelled) return;
        setMfaEnrolledByStaffId((prev) => {
          const next = { ...prev };
          entries.forEach(([key, enrolled]) => {
            next[key] = enrolled;
          });
          return next;
        });
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [users, canResetMfa]);

  const canEditUserPermissions =
    !!userData &&
    (isSystemAdmin ||
      hasPermission(userPermissions, "USER_MANAGEMENT") ||
      hasPermission(userPermissions, "UPDATE_USER"));
  const canEditRole =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "USER_MANAGEMENT"));

  const handleAdminCredAction = useCallback((row) => {
    setAdminCredForm({ open: true, row });
  }, []);

  const handleAdminCredFormClose = () => {
    setAdminCredForm({ open: false, row: null });
  };

  const handleAdminCredFormSubmit = ({ mode, value }) => {
    const { row } = adminCredForm;
    handleAdminCredFormClose();
    setAdminCredConfirm({ open: true, mode, row, value });
  };

  const handleAdminCredConfirmClose = () => {
    setAdminCredConfirm({ open: false, mode: null, row: null, value: "" });
  };

  const handleAdminCredConfirmYes = () => {
    const { mode, row, value } = adminCredConfirm;
    setPendingAdminCred({ mode, row, value });
    handleAdminCredConfirmClose();
    setAdminCredAuthOpen(true);
  };

  const handleAdminCredAuthClose = () => {
    setAdminCredAuthOpen(false);
    setPendingAdminCred(null);
  };

  const handleAdminCredAuthSubmit = async () => {
    if (!pendingAdminCred?.row || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }

    const { mode, row, value } = pendingAdminCred;
    const basePayload = {
      OrganizationId: userData.OrganizationId,
      StaffId: row.StaffId,
      CreatedBy: userData.StaffId,
    };

    try {
      showLoader(
        mode === "password" ? "Changing password..." : "Changing access code...",
      );
      const response =
        mode === "password"
          ? await adminChangeUserPassword({ ...basePayload, Password: value })
          : await adminChangeUserAccessCode({ ...basePayload, AccessCode: value });
      const parsed = parseAdminCredentialChangeResponse(response);

      setPendingAdminCred(null);
      setAdminCredAuthOpen(false);

      if (parsed.ok) {
        showAlert({
          title: "Success",
          message: parsed.message,
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message: parsed.message,
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Could not update credentials.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const orgHasQms = organizationHasQmsModule(userData);
  const orgHasQmsTrainingAdditionalRoles =
    organizationHasQmsTrainingAdditionalRolesFeature(userData);
  const canManageLibraryStaffAccess =
    orgHasQms &&
    (isSystemAdmin ||
      hasQmsActionPermission(userPermissions, "QMS_LIBRARY_SEE_STAFF"));
  const canManageMyLearningStaffAccess =
    orgHasQms &&
    (isSystemAdmin ||
      hasQmsActionPermission(userPermissions, "QMS_LIBRARY_ADD_STAFF"));

  const closeDocAccessModal = useCallback(() => {
    setDocAccessModal({
      open: false,
      mode: "library",
      row: null,
    });
  }, []);

  const openStaffDocumentAccess = useCallback(
    async (row, mode) => {
      if (!row?.StaffId || !userData?.OrganizationId) return;
      if (
        (mode === "library" && !canManageLibraryStaffAccess) ||
        (mode === "myLearning" && !canManageMyLearningStaffAccess)
      ) {
        return;
      }
      if (isSponsorCroUserRecord(row?.IsCRO) || isSponsorCroUserRecord(row?.IsCro)) {
        showAlert({
          title: "Not available",
          message: "Document access assignment is available for internal users only.",
          type: "warning",
        });
        return;
      }

      const fetchFn =
        mode === "library" ? fetchStaffDocumentLibraryDocuments : fetchStaffMyLearningDocuments;
      try {
        showLoader(
          mode === "library"
            ? "Checking document library access..."
            : "Loading My Learning access...",
        );
        const response = await fetchFn(userData, row.StaffId, {
          pageNumber: 1,
          pageSize: 1,
        });
        const story = firstSuccessStoryRow(response);

        if (!isApiSuccess(response, story)) {
          showAlert({
            title: mode === "library" ? "Document Library Access" : "My Learning Access",
            message:
              apiMessage(response, story) ||
              (mode === "library"
                ? "Assign this user Document Library permission first."
                : "Could not load My Learning documents."),
            type: "warning",
          });
          return;
        }

        setDocAccessModal({
          open: true,
          mode,
          row,
        });
      } catch (error) {
        showAlert({
          title: "Error",
          message: error?.message || "Failed to load document access.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [userData, showLoader, hideLoader, showAlert, canManageLibraryStaffAccess, canManageMyLearningStaffAccess],
  );

  const handleDocumentLibraryAccess = useCallback(
    (row) => openStaffDocumentAccess(row, "library"),
    [openStaffDocumentAccess],
  );

  const handleMyLearningAccess = useCallback(
    (row) => openStaffDocumentAccess(row, "myLearning"),
    [openStaffDocumentAccess],
  );

  const handleMfaResetClick = useCallback(
    async (row) => {
      if (!row?.StaffId) return;
      const key = staffMfaKey(row.StaffId);
      try {
        showLoader("Checking authenticator...");
        const response = await getMfaSettings(row.StaffId);
        const enrolled = isAuthenticatorEnrolled(response);
        setMfaEnrolledByStaffId((prev) => ({ ...prev, [key]: enrolled }));
        if (!enrolled) {
          showAlert({
            title: "No authenticator",
            message:
              "This user has not registered an MFA authenticator app. Reset is only available after authenticator setup.",
            type: "warning",
          });
          return;
        }
        setMfaResetConfirm({ open: true, row });
      } catch (err) {
        showAlert({
          title: "Unable to check MFA",
          message: err?.message || "Could not verify authenticator status.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, showAlert],
  );

  const handleMfaResetConfirmClose = () => {
    setMfaResetConfirm({ open: false, row: null });
  };

  const handleMfaResetConfirmYes = () => {
    const { row } = mfaResetConfirm;
    setPendingMfaReset(row);
    handleMfaResetConfirmClose();
    setMfaResetAuthOpen(true);
  };

  const handleMfaResetAuthClose = () => {
    setMfaResetAuthOpen(false);
    setPendingMfaReset(null);
  };

  const handleMfaResetAuthSubmit = async () => {
    if (!pendingMfaReset?.StaffId || !userData?.OrganizationId || !userData?.StaffId) {
      return;
    }
    const key = staffMfaKey(pendingMfaReset.StaffId);
    try {
      showLoader("Resetting authenticator...");
      const response = await resetAuthenticator({
        organizationId: userData.OrganizationId,
        staffId: pendingMfaReset.StaffId,
        createdBy: userData.StaffId,
      });
      setPendingMfaReset(null);
      setMfaResetAuthOpen(false);
      if (Number(response?.success) === 1) {
        setMfaEnrolledByStaffId((prev) => ({ ...prev, [key]: false }));
        showAlert({
          title: "Authenticator reset",
          message:
            response?.message ||
            "Authenticator cleared. The user can sign in with email OTP and register a new authenticator app.",
          type: "success",
        });
      } else {
        showAlert({
          title: "Reset failed",
          message: response?.message || "Could not reset authenticator.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Reset failed",
        message: err?.message || "Could not reset authenticator.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const tableActions = useMemo(() => {
    const list = [
      {
        label: "View",
        icon: "fas fa-eye",
        onClick: handleView,
      },
      {
        label: (row) => (isActive(row) ? "Deactivate" : "Activate"),
        icon: (row) => (isActive(row) ? "fas fa-ban" : "fas fa-check-circle"),
        onClick: handleToggleStatusClick,
        dividerBefore: true,
      },
    ];
    if (canChangeCredentials) {
      list.push({
        label: "Change credentials",
        icon: "fas fa-key",
        onClick: handleAdminCredAction,
        visible: (row) => isActive(row),
      });
    }
    if (canResetMfa) {
      list.push({
        label: "Reset MFA authenticator",
        icon: "fas fa-mobile-alt",
        onClick: handleMfaResetClick,
        // Only users with a registered authenticator app.
        visible: (row) =>
          isActive(row) &&
          mfaEnrolledByStaffId[staffMfaKey(row?.StaffId)] === true,
      });
    }
    if (canManageLibraryStaffAccess) {
      list.push({
        label: "Document Library Access",
        icon: "fas fa-folder-open",
        onClick: handleDocumentLibraryAccess,
        visible: (row) => isActive(row) && !isSponsorCroUserRecord(row?.IsCRO ?? row?.IsCro),
        dividerBefore: true,
      });
    }
    if (canManageMyLearningStaffAccess) {
      list.push({
        label: "My Learning Access",
        icon: "fas fa-graduation-cap",
        onClick: handleMyLearningAccess,
        visible: (row) => isActive(row) && !isSponsorCroUserRecord(row?.IsCRO ?? row?.IsCro),
        dividerBefore: !canManageLibraryStaffAccess,
      });
    }
    return list;
  }, [
    canChangeCredentials,
    canResetMfa,
    canManageLibraryStaffAccess,
    canManageMyLearningStaffAccess,
    handleAdminCredAction,
    handleMfaResetClick,
    handleDocumentLibraryAccess,
    handleMyLearningAccess,
    mfaEnrolledByStaffId,
  ]);

  const layoutStorageKey = useMemo(
    () =>
      userData?.StaffId
        ? `user-management-layout-v5-${userData.StaffId}`
        : "",
    [userData?.StaffId],
  );

  const roleFilterOptions = useMemo(
    () => buildUniqueFilterOptions(users, "Roles"),
    [users],
  );

  const departmentFilterOptions = useMemo(
    () => buildUniqueFilterOptions(users, "DepartmentName"),
    [users],
  );

  const statusFilterOptions = useMemo(
    () => buildUniqueStatusFilterOptions(users),
    [users],
  );

  const filteredUsers = useMemo(
    () => applyUserMgmtFilters(users, tableFilters),
    [users, tableFilters],
  );

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const getUserMgmtFieldLabel = useCallback((field) => {
    return (
      USER_MGMT_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field
    );
  }, []);

  const renderReportCell = useCallback((row, field) => {
    if (field === "Name") {
      return <UserMgmtIdentityCell row={row} onNameClick={handleView} />;
    }

    if (field === "EmailId") {
      return formatTruncatedUserMgmtCell(row.EmailId);
    }

    if (field === "DepartmentName") {
      return formatTruncatedUserMgmtCell(row.DepartmentName);
    }

    if (field === "Roles") {
      return formatTruncatedUserMgmtCell(row.Roles);
    }

    if (field === "Status") {
      const label = getUserMgmtStatusLabel(row);
      const active = label === "Active";
      return (
        <button
          type="button"
          className={`user-mgmt-report__status-btn is-${active ? "active" : "inactive"}`}
          onClick={() => handleToggleStatusClick(row)}
          title={active ? "Deactivate user" : "Activate user"}
          aria-label={
            active
              ? `Deactivate ${row.Name || "user"}`
              : `Activate ${row.Name || "user"}`
          }
        >
          {label === "—" ? "—" : active ? "Active" : "Inactive"}
        </button>
      );
    }

    if (field === "SystemDateTime") {
      return utils.formatDateTimeDisplay(row.SystemDateTime);
    }

    return String(row[field] ?? "—");
  }, []);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const raw = String(searchValue || "").trim();
    if (!raw) return true;

    const term = raw.toLowerCase();

    if (
      field === "Roles" ||
      field === "DepartmentName" ||
      field === "Status" ||
      field === "SystemDateTime" ||
      field === "rowActions"
    ) {
      return true;
    }

    if (field === "Name") {
      const combined = formatUserMgmtName(row).toLowerCase();
      return (
        combined.includes(term) ||
        String(row.Name ?? "")
          .toLowerCase()
          .includes(term) ||
        getUserEmployeeNum(row).toLowerCase().includes(term)
      );
    }

    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "Roles") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={roleFilterOptions}
            value={tableFilters.roles}
            onChange={(values) => updateTableFilters({ roles: values })}
            emptyLabel="All roles"
            addLabel="+"
            popoverTitle="Filter by role"
            popoverSubtitle="Select one or more roles"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "DepartmentName") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={departmentFilterOptions}
            value={tableFilters.departments}
            onChange={(values) => updateTableFilters({ departments: values })}
            emptyLabel="All departments"
            addLabel="+"
            popoverTitle="Filter by department"
            popoverSubtitle="Select one or more departments"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "Status") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search user-mgmt-report__column-select"
            value={tableFilters.status}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({ status: event.target.value })
            }
          >
            <option value="">All</option>
            {statusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <DateRangeFilter
            value={tableFilters.dateRange}
            onChange={(range) => updateTableFilters({ dateRange: range })}
            placeholder="Date range"
            presetOptions={USER_MGMT_COLUMN_DATE_PRESETS}
            compact
            disabled={options?.disabled}
          />
        );
      }

      if (field === "rowActions") {
        if (options?.checkOnly) return false;
        return null;
      }

      return null;
    },
    [
      departmentFilterOptions,
      roleFilterOptions,
      statusFilterOptions,
      tableFilters,
      updateTableFilters,
    ],
  );

  const getColumnHeaderClassName = useCallback(
    (field) => USER_MGMT_COLUMN_CLASS[field] || "",
    [],
  );

  const getColumnCellClassName = useCallback(
    (field) => USER_MGMT_COLUMN_CLASS[field] || "",
    [],
  );

  const getExportCellValue = useCallback((row, field) => {
    if (field === "ProfilePictureUrl" || field === "rowActions") return "";
    if (field === "Name") return formatUserMgmtName(row);
    if (field === "Status") return getUserMgmtStatusLabel(row);
    if (field === "SystemDateTime") {
      return utils.formatDateTimeDisplay(row.SystemDateTime);
    }
    return String(row[field] ?? "");
  }, []);

  const handleExportToExcel = useCallback(
    async ({ rows, columns } = {}) => {
      const exportRows = Array.isArray(rows) ? rows : filteredUsers;
      if (!exportRows.length) {
        showAlert({
          title: "Export",
          message: "No users to export.",
          type: "info",
        });
        return;
      }

      const exportColumns = (columns || DEFAULT_USER_MGMT_COLUMNS).filter(
        (field) => field !== "rowActions" && field !== "ProfilePictureUrl",
      );

      if (!exportColumns.length) {
        showAlert({
          title: "Export",
          message: "Add at least one data column before exporting.",
          type: "info",
        });
        return;
      }

      showLoader("Preparing Excel export...");
      try {
        const headers = exportColumns.map((field) => getUserMgmtFieldLabel(field));
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("User Management");
        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
        });

        exportRows.forEach((row) => {
          ws.addRow(exportColumns.map((field) => getExportCellValue(row, field)));
        });

        ws.columns = headers.map((header) => ({
          header,
          key: header,
          width: Math.max(14, header.length + 2),
        }));

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `user-management-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        showAlert({
          title: "Export failed",
          message: err?.message || "Could not export users to Excel.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [filteredUsers, showAlert, showLoader, hideLoader, getExportCellValue, getUserMgmtFieldLabel],
  );

  // const [fileUpload, setBothUpload] = useState([]);
  if (!userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "200px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading session...</span>
        </div>
      </div>
    );
  }

  //const steps = ["Basic", "Address", "Studies", "Documents", "Permissions"];
  const steps = ["Basic", "Address", "Studies"];

  return (
    <div className="user-mgmt-page user-mgmt-list-page">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "User Management" },
        ]}
        actions={[
          { icon: "fas fa-refresh", label: "Refresh", onClick: handleRefresh },
          {
            icon: "fas fa-file-excel",
            label: "Export Excel",
            onClick: () =>
              handleExportToExcel({
                rows: filteredUsers,
                columns: DEFAULT_USER_MGMT_COLUMNS,
              }),
          },
          { icon: "fas fa-plus", label: "Add User", onClick: () => handleEdit("") },
        ]}
      />

      <div className="user-mgmt-report-wrap">
        <ReportTable
          rows={filteredUsers}
          loading={usersLoading}
          fieldOptions={USER_MGMT_FIELD_OPTIONS}
          defaultColumns={DEFAULT_USER_MGMT_COLUMNS}
          getFieldLabel={getUserMgmtFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          getColumnHeaderClassName={getColumnHeaderClassName}
          getColumnCellClassName={getColumnCellClassName}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          rowActions={tableActions}
          hideColumnHeaderActionsFields={["Status", "SystemDateTime"]}
          persistLayoutKey={layoutStorageKey}
          onExportExcel={handleExportToExcel}
          exportLabel="Export Excel"
        />
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmYes}
        title={confirmModal.action === "deactivate" ? "Deactivate user" : "Activate user"}
        message={
          confirmModal.row
            ? `Do you want to ${confirmModal.action === "deactivate" ? "deactivate" : "activate"} ${confirmModal.row.Name || "this user"}?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass={confirmModal.action === "deactivate" ? "btn-danger" : "btn-success"}
      />

      <AuthModal
        show={authModalOpen}
        onClose={handleAuthClose}
        onSubmit={handleAuthSubmit}
        showDescription={false}
      />

      <AuthModal
        show={adminCredAuthOpen}
        onClose={handleAdminCredAuthClose}
        onSubmit={handleAdminCredAuthSubmit}
        showDescription={false}
      />

      <AdminChangeUserCredentialsModal
        open={adminCredForm.open}
        targetUser={adminCredForm.row}
        onClose={handleAdminCredFormClose}
        onSubmit={handleAdminCredFormSubmit}
      />

      <ConfirmModal
        isOpen={adminCredConfirm.open}
        onClose={handleAdminCredConfirmClose}
        onConfirm={handleAdminCredConfirmYes}
        title={
          adminCredConfirm.mode === "accessCode"
            ? "Change access code"
            : "Change password"
        }
        message={
          adminCredConfirm.row
            ? `Are you sure you want to change the ${
                adminCredConfirm.mode === "accessCode" ? "access code" : "password"
              } for ${adminCredConfirm.row.Name || "this user"}?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-primary"
      />

      <ConfirmModal
        isOpen={mfaResetConfirm.open}
        onClose={handleMfaResetConfirmClose}
        onConfirm={handleMfaResetConfirmYes}
        title="Reset MFA authenticator?"
        message={
          mfaResetConfirm.row
            ? `Reset the authenticator app for ${
                mfaResetConfirm.row.Name || "this user"
              }? Use this when their phone was lost. They will sign in with email OTP next time and can register a new authenticator app.`
            : ""
        }
        confirmText="Yes, reset"
        cancelText="Cancel"
        confirmButtonClass="btn-warning"
        icon="fas fa-mobile-alt text-warning"
      />

      <AuthModal
        show={mfaResetAuthOpen}
        onClose={handleMfaResetAuthClose}
        onSubmit={handleMfaResetAuthSubmit}
        showDescription={false}
      />

      <UserStaffDocumentAccessModal
        open={docAccessModal.open}
        mode={docAccessModal.mode}
        staffRow={docAccessModal.row}
        userData={userData}
        showLoader={showLoader}
        hideLoader={hideLoader}
        showAlert={showAlert}
        onClose={closeDocAccessModal}
      />

      <InternalUserAddEditModal
        open={isUserPopupOpen && !isExternalUser}
        formData={formData}
        fieldErrors={fieldErrors}
        roles={roles}
        visibleRoles={visibleRoles}
        department={department}
        siteOptions={siteOptions}
        study={study}
        maxDate={maxDate}
        userData={userData}
        canEditUserPermissions={canEditUserPermissions}
        canEditRole={canEditRole}
        permissionLayout="modules"
        onClose={handleClose}
        onSave={handleUserSaveUpdate}
        onFormInput={handleFormInput}
        onDobChange={handlDOB}
        onStartDateChange={handlStartDate}
        onAccessDateChange={handlAccessDate}
        onGenderChange={handleGenderChange}
        onRoleSelect={handleRoles}
        onUserPermissionsChange={handleUserPermissionsChange}
        onDepartmentSelect={handleDepartment}
        onSitesSelect={handleSites}
        onProfilePictureChange={(base64) =>
          setFormData((prev) => ({ ...prev, profilePicture: base64 }))
        }
        onStudiesChange={(studies) => {
          clearFieldError("studies");
          setFormData((prev) => ({ ...prev, studies }));
        }}
        showQmsTrainingAdditionalRoles={orgHasQmsTrainingAdditionalRoles}
        qmsTrainingAdditionalRoleOptions={qmsTrainingAdditionalRoleOptions}
        onQmsTrainingAdditionalRolesSelect={handleQmsTrainingAdditionalRoles}
      />

      {isUserPopupOpen && isExternalUser && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header" style={{ padding: "0.75rem 1rem" }}>
                <h5 className="modal-title" style={{ fontSize: "1rem", margin: 0 }}>User Add/Edit</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </div>

              <div className="modal-body" style={{ padding: "1rem", maxHeight: "70vh", overflowY: "auto" }}>
                <div className="user-form-tabs mb-2">
                  {steps.map((stepLabel, idx) => {
                    const stepNo = idx + 1;
                    const isActive = currentStep === stepNo;
                    return (
                      <button
                        key={stepLabel}
                        type="button"
                        className={`user-form-tab ${isActive ? "active" : ""}`}
                        onClick={() => {
                          if (currentStep === 1 && stepNo !== 1) {
                            const errorMap = getBasicInfoValidationErrorMap();
                            if (Object.keys(errorMap).length > 0) {
                              setFieldErrors(errorMap);
                              return;
                            }
                          }
                          setCurrentStep(stepNo);
                        }}
                      >
                        {stepLabel}
                      </button>
                    );
                  })}
                </div>
                <hr style={{ margin: "0.75rem 0" }} />

                {currentStep === 1 && (
                  <>
                    <div className="row">
                      <div className="col-md-3">
                        <label>
                          First Name <span className="mandatory">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleFormInput}
                          name="firstName"
                          value={formData.firstName || ""}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>
                          Last Name <span className="mandatory">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleFormInput}
                          name="lastName"
                          value={formData.lastName || ""}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>
                          Contact Number
                        </label>
                        <div className="um-phone-input">
                          <select
                            className="form-control um-phone-input__dial"
                            value={orgMobileDialLabel(
                              formData.mobileCountryIso2 ||
                                orgCountryIso2 ||
                                "GB",
                            )}
                            disabled
                            aria-label="Organisation country dial code"
                          >
                            <option
                              value={orgMobileDialLabel(
                                formData.mobileCountryIso2 ||
                                  orgCountryIso2 ||
                                  "GB",
                              )}
                            >
                              {orgMobileDialLabel(
                                formData.mobileCountryIso2 ||
                                  orgCountryIso2 ||
                                  "GB",
                              )}
                            </option>
                          </select>
                          <input
                            type="text"
                            className="form-control"
                            name="contactNumber"
                            value={formData.contactNumber || ""}
                            onChange={(e) => {
                              const iso =
                                formData.mobileCountryIso2 ||
                                orgCountryIso2 ||
                                "GB";
                              const { national } = normalizeNationalMobileInput(
                                e.target.value,
                                iso,
                              );
                              setFormData((prev) => ({
                                ...prev,
                                contactNumber: national,
                              }));
                            }}
                            onPaste={(e) => {
                              const text =
                                e.clipboardData?.getData("text") ?? "";
                              e.preventDefault();
                              const iso =
                                formData.mobileCountryIso2 ||
                                orgCountryIso2 ||
                                "GB";
                              const { national } = normalizeNationalMobileInput(
                                text,
                                iso,
                              );
                              setFormData((prev) => ({
                                ...prev,
                                contactNumber: national,
                              }));
                            }}
                            inputMode="numeric"
                            autoComplete="tel-national"
                            placeholder="7123456789"
                          />
                        </div>
                      </div>
                      <div className="col-md-3 d-flex justify-content-center">
                        {/* <FileUpload
                          acceptedFormats={[
                            ".pdf",
                            ".doc",
                            ".docx",
                            ".jpg",
                            ".jpeg",
                            ".png",
                          ]}
                          maxFileSize={5 * 1024 * 1024}
                          maxTotalSize={30 * 1024 * 1024}
                          multiple={false}
                          outputFormat="both"
                          onChange={setBothUpload}
                          placeholder="Upload files with complete data"
                        /> */}

                        {/* <input
                          type="file"
                          className="form-control"
                          name="profilePicture"
                          value={formData.profilePicture || ""}
                          onChange={handleFormInput}
                        /> */}

                        <SimpleCircularUpload
                          defaultImage="data:image/png;base64,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"
                          currentImage={formData.profilePicture}
                          size={80}
                          maxFileSize={5}
                          onChange={(base64) => {
                            // Handle base64 string
                            setFormData((prev) => ({
                              ...prev,
                              profilePicture: base64,
                            }));
                          }}
                        />
                      </div>
                    </div>

                    <div className="row mt-2">
                      <div className="col-md-3">
                        <label>
                          Employee Number <span className="mandatory">*</span>
                        </label>
                        <div className="span-under-input">
                          <span>{formData.prefix}</span>
                          <input
                            type="text"
                            className="form-control"
                            name="employeeNumber"
                            value={formData.employeeNumber || ""}
                            onChange={handleFormInput}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label>
                          Email <span className="mandatory">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="email"
                          value={formData.email || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>
                          Confirm Email <span className="mandatory">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control${fieldErrors.confirmEmail ? " is-invalid" : ""}`}
                          name="confirmEmail"
                          value={formData.confirmEmail || ""}
                          onChange={handleFormInput}
                        />
                        {fieldErrors.confirmEmail ? (
                          <div className="user-form-field-error">
                            {fieldErrors.confirmEmail}
                          </div>
                        ) : null}
                      </div>
                      <div className="col-md-3">
                        <label>DOB</label>
                        <DatePicker
                          selected={formData.dob}
                          onChange={handlDOB}
                          placeholderText="Select date"
                          className="form-control"
                          dateFormat="dd/MM/yyyy"
                          isClearable
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          maxDate={new Date()}
                          onChangeRaw={(e) => e.preventDefault()}
                          popperProps={{ strategy: "fixed" }}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-3">
                        <label>Start Date</label>
                        <DatePicker
                          selected={formData.startDate}
                          onChange={handlStartDate}
                          placeholderText="Select date"
                          className="form-control"
                          dateFormat="dd/MM/yyyy"
                          isClearable
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          minDate={new Date()}
                          maxDate={maxDate}
                          onChangeRaw={(e) => e.preventDefault()}
                          popperProps={{ strategy: "fixed" }}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Access Date</label>
                        <DatePicker
                          selected={formData.accessDate}
                          onChange={handlAccessDate}
                          placeholderText="Select date"
                          className="form-control"
                          dateFormat="dd/MM/yyyy"
                          isClearable
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          minDate={new Date()}
                          maxDate={maxDate}
                          onChangeRaw={(e) => e.preventDefault()}
                          popperProps={{ strategy: "fixed" }}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Gender</label>
                        <GenderSelector
                          value={formData.gender}
                          onChange={handleGenderChange}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>
                          Role <span className="mandatory">*</span>
                        </label>
                        <Dropdown
                          options={visibleRoles}
                          isMulti={false}
                          selected={formData.role}
                          onSelect={handleRoles}
                          disabled={!canEditRole}
                        />
                      </div>
                      {orgHasQmsTrainingAdditionalRoles && !isExternalUser && (
                        <div className="col-md-6">
                          <label>QMS Training Additional Roles</label>
                          <Dropdown
                            options={qmsTrainingAdditionalRoleOptions}
                            isMulti={true}
                            selected={formData.qmsTrainingAdditionalRoles || []}
                            onSelect={handleQmsTrainingAdditionalRoles}
                          />
                          <small className="text-muted d-block mt-1">
                            For SOP and training assignment only. Does not change
                            login role or permissions.
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-md-3">
                        <label>Department</label>
                        <Dropdown
                          options={department}
                          isMulti={false}
                          selected={formData.department}
                          onSelect={handleDepartment}
                        />
                      </div>
                      {!isExternalUser && !isSystemAdministratorRole(formData.role) && (
                        <div className="col-md-6">
                          <label>
                            Sites <span className="mandatory">*</span>
                          </label>
                          <Dropdown
                            options={siteOptions}
                            isMulti={true}
                            selected={formData.selectedSites || []}
                            onSelect={handleSites}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    <div className="row">
                      <div className="col-md-12">
                        <textarea
                          className="w-100 form-control"
                          name="addressLine1"
                          value={formData.addressLine1 || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <label>Postal Code</label>
                        <input
                          type="text"
                          className="form-control"
                          name="postalCode"
                          value={formData.postalCode || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>City</label>
                        <input
                          type="text"
                          className="form-control"
                          name="city"
                          value={formData.city || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>County</label>
                        <input
                          type="text"
                          className="form-control"
                          name="county"
                          value={formData.county || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Country</label>
                        <input
                          type="text"
                          className="form-control"
                          name="country"
                          value={formData.country || ""}
                          onChange={handleFormInput}
                        />
                      </div>
                    </div>
                  </>
                )}

                {currentStep === 3 && (
                  <div className="row">
                    <div className="col-md-12">
                      {isExternalUser ? (
                        <>
                          {externalScoping.renderStudiesStep()}
                          {fieldErrors.studies ? (
                            <div className="user-form-field-error">
                              {fieldErrors.studies}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <DualListBox
                          initialSelectedIds={selectedStudyIdsForDualList}
                          allItems={study}
                          onChange={(selected) => {
                            const normalized = (selected || []).map((item) => ({
                              StudyId: item.value,
                              label: item.label,
                            }));
                            setFormData((prev) => ({
                              ...prev,
                              studies: normalized,
                            }));
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

              </div>

              <div className="modal-footer" style={{ padding: "0.75rem 1rem" }}>
                <div className="row">
                  <div className="col-md-12">
                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={handleClose}
                    >
                      <i className="fas fa-close me-2"></i>
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleUserSaveUpdate}
                    >
                      {formData.StaffId === "" ? (
                        <>
                          <i className="fas fa-plus me-2"></i>
                          Add
                        </>
                      ) : (
                        <>
                          <i className="fas fa-edit me-2"></i>
                          Update
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
